import { useEffect, useState } from 'react';
import { settleOrderFrom, type SettleOrder } from './settle';
import { fa, toman } from './fa';
import { api } from './api';

/*
 * The open tables, and the only place in the shop a table's bill can be paid.
 *
 * A branch with a till tells the tablet not to settle — a gapless invoice
 * sequence must have exactly one owner, and where there is a counter that owner
 * is the counter. But nothing here could settle either: this app posts baskets
 * to /sales and had no notion of a table at all. So configuring a café the way
 * it actually works produced a shop where the kitchen cooked and nobody could
 * take the money.
 *
 * What the till adds over the tablet is the till: the drawer, the card reader,
 * the person whose shift the takings belong to. The bill itself is already
 * priced — the rounds were priced when they were fired, and a price change at
 * nine must not reprice what a table ate at seven — so this screen never
 * computes a total. It shows what the agent says is owed and takes payment.
 */

/*
 * What the payment screen settles.
 *
 * An alias rather than a second declaration: settleOrderFrom builds this, and
 * two structurally identical types would drift the moment one of them gained a
 * field. Kept exported under this name because the screens already speak it.
 */
export type SettleTarget = SettleOrder;

export type FloorTable = {
  id: string;
  label: string;
  seats: number;
  zone: string;
  occupied: boolean;
  check?: {
    id: string;
    number: string;
    openedAt: string;
    guestCount: number;
    totalRial: number;
  } | null;
};

type Round = {
  id: string;
  sequence: number;
  status: string;
  sentAt: string;
  note: string;
  lines: { name: string; quantityMilli: number; lineTotalRial: number }[];
};

type CheckDetail = {
  id: string;
  number: string;
  tableLabel?: string;
  guestCount: number;
  /** The bill as it is shown. */
  totalRial: number;
  /*
   * What the agent will actually charge — the number the payment screen must
   * collect, because a settle whose payments do not add up to it is refused.
   *
   * It is not always totalRial: the bill excludes the VAT the invoice adds.
   * Absent from an older agent, where totalRial is the only figure there is.
   */
  payableRial?: number;
  rounds: Round[];
};

export function TablesScreen({ onBack, onSettle }: {
  onBack: () => void;
  onSettle: (target: SettleTarget) => void;
}) {
  const [tables, setTables] = useState<FloorTable[]>([]);
  const [open, setOpen] = useState<CheckDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState<string | null>(null);

  /*
   * Printing the bill is a separate act from paying it, and deliberately does
   * not move the screen on.
   *
   * A guest asks what they owe, reads it, and then pays — often after another
   * round. Sending the cashier to the payment screen because they printed a
   * bill would make the common case (print, wait, print again) feel like an
   * error, and a bill is not a claim on the table.
   *
   * A failure is shown rather than swallowed. The cook has a screen to fall
   * back on and the guest has nothing, so a cashier who is not told the paper
   * never came out stands at the printer waiting for it.
   */
  const printBill = async (reprint: boolean) => {
    if (!open) return;
    setPrinting(true);
    setError(null);
    setPrinted(null);
    try {
      await api.post(`/floor/checks/${encodeURIComponent(open.id)}/bill`, { reprint });
      setPrinted('صورتحساب چاپ شد');
    } catch (e) {
      setError((e as Error)?.message ?? 'چاپ صورتحساب انجام نشد');
    } finally {
      setPrinting(false);
    }
  };

  /*
   * Polled, like arrivals. A waiter adds a round while the guest is at the
   * counter paying, and a total that was read once is the wrong total.
   */
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get<{ tables: FloorTable[] }>('/floor/tables');
        if (alive) setTables(res.tables ?? []);
      } catch {
        /* the chip in the header already says the agent is unreachable */
      }
    };
    void load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const openCheck = async (table: FloorTable) => {
    if (!table.check) return;
    setError(null);
    try {
      setOpen(await api.get<CheckDetail>(`/floor/checks/${encodeURIComponent(table.check.id)}`));
    } catch (e) {
      setError((e as Error)?.message ?? 'صورت‌حساب باز نشد');
    }
  };

  /*
   * Paying a table is the counter's payment screen, not a pair of buttons here.
   *
   * This screen used to POST the settle itself with a method name — «نقدی» or
   * «کارتخوان» — and «کارتخوان» recorded a card payment that no terminal had
   * ever been asked to take. The invoice said paid, the reader had never seen
   * the amount, and the bank had no matching transaction. That is the exact
   * failure the walk-in payment screen was hardened against: it will not let a
   * cashier confirm a card sale until the reader has answered, and it holds a
   * basket the reader answered ambiguously about rather than guessing.
   *
   * A table's bill is money owed in the same sense a basket is. So it goes to
   * the same screen, and the agent already expects that — SettleRequest carries
   * `payments`, the itemised shape /sales posts, precisely so a till can settle
   * a table through the counter's own flow. Only this screen had not used it.
   *
   * The amount still never travels from here. What is handed over is what the
   * agent said is owed, and the agent recomputes it from the rounds and refuses
   * a settle that pays less.
   */
  const toPayment = () => {
    if (!open) return;
    // settleOrderFrom, not an inline object: an order opened from
    // «سفارش‌های ورودی» reaches payment by another route now, and the amount
    // must not depend on which route a cashier took.
    onSettle(settleOrderFrom(open));
  };

  const busyTables = tables.filter((t) => t.occupied && t.check);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 20, gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            height: 40, padding: '0 16px', borderRadius: 10, cursor: 'pointer',
            border: '1px solid var(--luma-line)', background: 'var(--luma-card)',
            fontSize: 14, fontWeight: 700,
          }}
        >
          بازگشت
        </button>
        <div style={{ fontSize: 20, fontWeight: 800 }}>میزهای باز</div>
        <div style={{ marginInlineStart: 'auto', color: 'var(--luma-ink-secondary)', fontSize: 14 }}>
          {fa(busyTables.length)} میز
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, fontSize: 14, fontWeight: 700,
          background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
          color: 'var(--luma-tint-danger-text)',
        }}>
          {error}
        </div>
      )}

      {busyTables.length === 0 && (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--luma-ink-secondary)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>میز بازی نیست</div>
            <div style={{ fontSize: 14, marginTop: 6 }}>
              میزهایی که گارسون باز کرده این‌جا ظاهر می‌شوند
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, overflowY: 'auto' }}>
        {busyTables.map((t) => (
          <button
            key={t.id}
            onClick={() => void openCheck(t)}
            style={{
              width: 240, textAlign: 'start', cursor: 'pointer',
              padding: 14, borderRadius: 14, background: 'var(--luma-card)',
              border: '1px solid var(--luma-line)',
            }}
          >
            <div style={{ fontSize: 19, fontWeight: 800 }}>{t.label}</div>
            <div style={{ fontSize: 13, color: 'var(--luma-ink-secondary)', marginTop: 4 }}>
              سفارش {fa(t.check!.number)} · {fa(t.check!.guestCount)} مهمان
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, marginTop: 10 }}>
              {toman(t.check!.totalRial)}
            </div>
          </button>
        ))}
      </div>

      {open && (
        <CheckSheet
          detail={open}
          onClose={() => { setOpen(null); setPrinted(null); }}
          onSettle={toPayment}
          onPrint={printBill}
          printing={printing}
          printed={printed}
        />
      )}
    </div>
  );
}

function CheckSheet({
  detail, onClose, onSettle, onPrint, printing, printed,
}: {
  detail: CheckDetail;
  onClose: () => void;
  onSettle: () => void;
  onPrint: (reprint: boolean) => void;
  printing: boolean;
  printed: string | null;
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        display: 'grid', placeItems: 'center', zIndex: 40,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560, maxHeight: '80vh', overflowY: 'auto', background: 'var(--luma-card)',
          borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>
            {detail.tableLabel || `سفارش ${fa(detail.number)}`}
          </div>
          <div style={{ fontSize: 13, color: 'var(--luma-ink-secondary)' }}>
            سفارش {fa(detail.number)} · {fa(detail.guestCount)} مهمان
          </div>
        </div>

        {detail.rounds.map((r) => (
          <div key={r.id} style={{ borderTop: '1px solid var(--luma-line)', paddingTop: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--luma-ink-secondary)', marginBottom: 6 }}>
              دور {fa(r.sequence)}
              {r.status === 'voided' ? ' — لغو شده' : ''}
            </div>
            {r.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>
                  {fa(l.quantityMilli / 1000)} × {l.name}
                </span>
                <span style={{ fontSize: 14 }}>{toman(l.lineTotalRial)}</span>
              </div>
            ))}
          </div>
        ))}

        <div style={{
          borderTop: '2px solid var(--luma-line)', paddingTop: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>جمع کل</span>
          <span style={{ fontSize: 22, fontWeight: 800 }}>{toman(detail.totalRial)}</span>
        </div>

        {printed && (
          <div style={{
            padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 700, marginTop: 4,
            background: 'var(--luma-tint-success-bg)', border: '1px solid var(--luma-tint-success-border)',
            color: 'var(--luma-tint-success-text)',
          }}>
            {printed}
          </div>
        )}

        {/*
          * The bill first and the payment second, in the order a table is
          * served: the guest asks what they owe, reads it, and then pays.
          * Quieter than «رفتن به پرداخت» because printing is the errand and
          * taking the money is the act.
          */}
        <button
          onClick={() => onPrint(printed !== null)}
          disabled={printing}
          style={{
            height: 52, borderRadius: 12, cursor: printing ? 'default' : 'pointer', marginTop: 4,
            border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
            fontSize: 16, fontWeight: 700, opacity: printing ? 0.6 : 1,
          }}
        >
          {printing ? 'در حال چاپ…' : printed ? 'چاپ دوبارهٔ صورتحساب' : 'چاپ صورتحساب'}
        </button>

        <button
          onClick={onSettle}
          style={{
            height: 56, borderRadius: 12, cursor: 'pointer',
            border: 'none', background: 'var(--luma-accent)', color: '#fff',
            fontSize: 17, fontWeight: 800,
          }}
        >
          رفتن به پرداخت
        </button>
        <button
          onClick={onClose}
          style={{
            height: 42, borderRadius: 10, cursor: 'pointer', background: 'transparent',
            border: '1px solid var(--luma-line)', fontSize: 14, fontWeight: 700,
          }}
        >
          بستن
        </button>
      </div>
    </div>
  );
}
